import maplibregl from "maplibre-gl";
import "maplibre-gl/dist/maplibre-gl.css";

// OpenFreeMap 'liberty' style — OSM-based vector tiles, no API key, no cap.
// (Runner-up: self-hosted Protomaps PMTiles — a one-line style-URL swap.)
const STYLE_URL = "https://tiles.openfreemap.org/styles/liberty";

// The OpenFreeMap 'liberty' style ships NO `attribution` on its sources, so the
// MapLibre AttributionControl has nothing to show by default. We inject the
// required credits ourselves (basemap + our overlay's data lineage). Kept
// tasteful/compact; the full provenance lives in the About panel.
//
//   Basemap:  OpenFreeMap (OpenMapTiles schema) © OpenStreetMap contributors
//   Land mask: Natural Earth (public domain)
//   Pollen:    polleninformation.at / EAN, © 2012 EAN (re-presented, data owned)
const ATTRIBUTION_HTML =
  '<a href="https://openfreemap.org/" target="_blank" rel="noopener">OpenFreeMap</a> ' +
  '(<a href="https://openmaptiles.org/" target="_blank" rel="noopener">OpenMapTiles</a>) ' +
  '© <a href="https://www.openstreetmap.org/copyright" target="_blank" rel="noopener">OpenStreetMap contributors</a> | ' +
  '<a href="https://www.naturalearthdata.com/" target="_blank" rel="noopener">Natural Earth</a> | ' +
  'Pollen data: <a href="https://www.polleninformation.at/" target="_blank" rel="noopener">polleninformation.at</a> / EAN (© 2012 EAN)';

// Europe view matching the source raster's approximate extent
// (~10W..45E, ~35N..72N). Centered on central Europe.
export const EUROPE_CENTER: [number, number] = [15, 54];
export const EUROPE_ZOOM = 3.4;

export interface MapInit {
  /** [lng, lat] — overrides the default Europe centre (e.g. from a shared URL). */
  center?: [number, number];
  /** Zoom — overrides the default (e.g. from a shared URL). */
  zoom?: number;
}

export function createMap(
  container: string | HTMLElement,
  init: MapInit = {},
): maplibregl.Map {
  const map = new maplibregl.Map({
    container,
    style: STYLE_URL,
    center: init.center ?? EUROPE_CENTER,
    zoom: init.zoom ?? EUROPE_ZOOM,
    minZoom: 2,
    // C2 fix: cap zoom to the data's useful resolution (~5 km/px). At z7 one
    // source cell is ~7 screen px (legible with the hi-res coast mask); beyond
    // that the categorical grid reads as opaque blocks. See qa/fix-spec-c1c2.md.
    maxZoom: 7,
    // Compact (collapsible) attribution: an (i)-style toggle on mobile, expanded
    // on wide screens. `customAttribution` injects our credits since the
    // OpenFreeMap style carries none. See ATTRIBUTION_HTML above.
    attributionControl: {
      compact: true,
      customAttribution: ATTRIBUTION_HTML,
    },
    // Allow GL canvas readback for the verification harnesses (P2 alignment
    // metric, P6 rounds). Minor perf cost; acceptable for a spike. The shipped
    // build can drop this if pixel-readback verification is not required.
    canvasContextAttributes: { preserveDrawingBuffer: true },
  });

  // Full-screen-map layout (UX spec §2): the [i]/about button floats top-right,
  // so the zoom NavigationControl goes top-LEFT (out of the thumb path and clear
  // of the about button). The scale bar goes bottom-left, above the timeline
  // card. Attribution stays bottom-right (compact) via the map options above.
  map.addControl(new maplibregl.NavigationControl({ showCompass: false }), "top-left");
  map.addControl(new maplibregl.ScaleControl({ unit: "metric" }), "bottom-left");

  return map;
}
